import { useCallback, useContext, useMemo, useState } from 'react';
import {
  CloudDownloadOutlined,
  DeleteOutline as DeleteIcon,
  MoreVert,
} from '@mui/icons-material';
import { Box, Chip, IconButton, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { DocumentReferencesView } from 'api';
import { AI_GRADIENT_STOPS, aiGradientTextSx } from 'common/constants/ai';
import { DELETE_REFERENCE_CUSTOM_MESSAGE, FILE_NAME } from 'common/constants/documents';
import * as messages from 'common/constants/messages/validations';
import { ConfirmationDelete } from 'components/Dialogs';
import { DialogContext, LayoutContext } from 'context';
import useDocumentsContext from 'context/DocumentsContext';
import AISuggestionPopover from 'pages/Documents/components/SuggestedDocumentReference/AISuggestionPopover';
import FilesService from 'services/documents';
import { useDocuments, useResponse } from 'services/hooks';
import { Logger } from 'utilities';
import getCancelDialogOptions from 'utilities/getCancelDialogOptions';
import { ReferencedDocumentProps } from './ReferencedDocument.props';
import useGetFileIconByExtension from '../../../../utilities/getFileIconByExtension/getFileIconByExtension';
import { PREVIEWABLE_DOC_TYPES } from '../../../Drawer/document-preview-drawer-tab/document-preview.constants';

const ReferencedDocument = ({
  document,
  selectedMeasurementDateId,
  currentPage,
  isDisabled,
  areDocumentsActionsDisabled,
  onClick,
  citationMetadata,
  citationNumber,
}: ReferencedDocumentProps) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const filesService = useMemo(() => new FilesService(), []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const { viewOnlyUser } = useContext(LayoutContext);
  const dialogContext = useContext(DialogContext);
  const { documents, updateReferencedDocuments } = useDocumentsContext();
  const { addDocumentReferencesObject, downloadDocument, setIsLoading } = useDocuments();
  const { errorNotification, successNotification } = useResponse();
  
  const isPendingApproval = (document as any).pending_auto_classification_approval;
  const aiClassification = (document as any).ai_classification;
  const isPreviewableFileType = useMemo(
    () => PREVIEWABLE_DOC_TYPES.includes(document.file.file_type),
    [document.file.file_type]
  );
  const currentReference = useMemo(
    () => document.referenced_in?.find(item => item.reference_object_type === currentPage),
    [document.referenced_in, currentPage]
  );

  const getReferencesData = (referencedFeatureId: number) => ({
    create_list: [],
    delete_list: [referencedFeatureId],
  });

  const handleReferences = async (): Promise<void> => {
    try {
      // Get id of the corresponding reference
      const referencedFeatureId = currentReference?.id;
      if (!referencedFeatureId) {
        Logger.error('Referenced feature ID not found', {
          document,
          currentPage,
        });
        return;
      }
      const updatedReferenceData = getReferencesData(referencedFeatureId);
      const requestData = {
        ...updatedReferenceData,
        measurement_date_id: selectedMeasurementDateId,
      };
      await addDocumentReferencesObject(selectedMeasurementDateId, requestData);
      await updateReferencedDocuments(documents, currentPage);
      queryClient.invalidateQueries({
        queryKey: ['documents'],
        exact: false,
      });
      successNotification(messages.REFERENCE_DELETED_SUCCESS);
    } catch (error) {
      errorNotification(messages.REFERENCE_DELETED_ERROR);
      Logger.error('Error deleting reference', { error });
    }
  };

  const handleClick = async (): Promise<void> => {
    const { showDialog } = dialogContext;
    const ContentComponent = (
      <ConfirmationDelete customMessage={DELETE_REFERENCE_CUSTOM_MESSAGE(document)} itemName={FILE_NAME(document)} />
    );

    showDialog(
      getCancelDialogOptions(
        () => ContentComponent,
        () => handleReferences()
      )
    );
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    handleClick();
  };

  const handleDownload = useCallback(
    async (documentParam: DocumentReferencesView) => {
      setIsLoading(true);
      await downloadDocument(documentParam);
      setIsLoading(false);
    },
    [downloadDocument, setIsLoading]
  );

  const handleAcceptClassification = useCallback(async () => {
    try {
      await filesService.approveClassification(String(document.document_reference_id));
      enqueueSnackbar('Classification suggestion accepted', { variant: 'success' });
      updateReferencedDocuments?.(documents);
      queryClient.invalidateQueries({ queryKey: ['documents'], exact: false });
    } catch (error) {
      Logger.error('Error accepting classification', error)
      enqueueSnackbar('Failed to accept classification', { variant: 'error' });
    }
  }, [filesService, document.document_reference_id, enqueueSnackbar, updateReferencedDocuments, documents, queryClient]);

  const handleDeclineClassification = useCallback(async () => {
    try {
      await filesService.declineClassification(String(document.document_reference_id));
      enqueueSnackbar('Classification suggestion dismissed', { variant: 'success' });
      updateReferencedDocuments?.(documents);
      queryClient.invalidateQueries({ queryKey: ['documents'], exact: false });
    } catch (error) {
      Logger.error('Error accepting classification', error)
      enqueueSnackbar('Failed to dismiss classification', { variant: 'error' });
    }
  }, [filesService, document.document_reference_id, enqueueSnackbar, updateReferencedDocuments, documents, queryClient]);

  const handleDocumentClick = useCallback(() => {
    if (onClick && isPreviewableFileType && currentReference) {
      onClick(currentReference);
    } else {
      handleDownload(document);
    }
  }, [onClick, isPreviewableFileType, document, handleDownload, currentReference]);

  const formatCitationTooltip = () => `Document: ${document.file.filename}.${document.file.file_type}`;

  const gradientId = `ai-gradient-border-ref-${document.document_reference_id}`;
  const fileIcon = useGetFileIconByExtension(document.file.file_type);

  const documentRow = (
    hoverHandlers?: {
      onMouseEnter: (event: React.MouseEvent<HTMLElement>) => void;
      onMouseLeave: () => void;
      ariaDescribedBy: string | undefined;
    }
  ) => (
    <Box
      sx={{ position: 'relative', width: 'fit-content', maxWidth: '100%' }}
      aria-describedby={hoverHandlers?.ariaDescribedBy}
      onMouseEnter={hoverHandlers?.onMouseEnter}
      onMouseLeave={hoverHandlers?.onMouseLeave}>
      {isPendingApproval && (
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              {AI_GRADIENT_STOPS.map(stop => (
                <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
              ))}
            </linearGradient>
          </defs>
          <rect
            x="1"
            y="1"
            rx="8"
            ry="8"
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth="2"
            strokeDasharray="6 4"
            style={{ width: 'calc(100% - 2px)', height: 'calc(100% - 2px)' }}
          />
        </svg>
      )}
      <Stack
        sx={{
          width: 'fit-content',
          maxWidth: '100%',
          padding: isPendingApproval ? '0.5rem' : 0,
          '&:hover': {
            cursor: 'pointer',
          },
        }}
        direction="row"
        spacing={2}
        alignItems="center"
        aria-label="Referenced document">
        {citationNumber !== undefined && (
          <Tooltip
            title={
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>{formatCitationTooltip()}</pre>
            }
            placement="top">
            <Chip
              label={`[${citationNumber}]`}
              size="small"
              sx={{
                height: '20px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '& .MuiChip-label': {
                  padding: '0 6px',
                },
              }}
            />
          </Tooltip>
        )}
        {fileIcon}
        <Tooltip
          disableHoverListener={areDocumentsActionsDisabled}
          title={isPreviewableFileType ? 'Click to preview.' : 'Click to download. (preview coming soon)'}
          placement="top-start">
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="baseline">
                <span>{FILE_NAME(document)}</span>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                  {`Uploaded on ${moment(document.file.uploaded_date).format('MMM D, YYYY')}`}
                </Typography>
              </Stack>
            }
            onClick={areDocumentsActionsDisabled ? undefined : () => handleDocumentClick()}
            sx={{
              '& .MuiListItemText-primary': {
                fontWeight: isPendingApproval ? 500 : 400,
                ...(isPendingApproval ? aiGradientTextSx as Record<string, unknown> : {}),
              },
            }}
          />
        </Tooltip>

        {isPendingApproval ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
            <IconButton
              disabled={isDisabled || viewOnlyUser}
              onClick={() => handleDownload(document)}
              aria-label="Download"
              size="small">
              <CloudDownloadOutlined fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
        /* Normal Document Actions */
          <Stack direction="row" spacing={2}>
            <IconButton
              disabled={isDisabled || viewOnlyUser}
              onClick={() => handleDownload(document)}
              aria-label="Download"
              size="small">
              <CloudDownloadOutlined fontSize="small" />
            </IconButton>
            <IconButton
              disabled={isDisabled || viewOnlyUser}
              onClick={handleMenuClick}
              aria-label="More actions"
              size="small">
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              slotProps={{
                list: {
                  'aria-labelledby': 'more-actions-button',
                },
              }}>
              <MenuItem onClick={handleDeleteClick}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
              </MenuItem>
            </Menu>
          </Stack>
        )}
      </Stack>
    </Box>
  );

  if (isPendingApproval) {
    return (
      <AISuggestionPopover
        documentReferenceId={document.document_reference_id}
        aiClassification={aiClassification}
        referencedIn={document.referenced_in}
        onAccept={handleAcceptClassification}
        onDecline={handleDeclineClassification}
      >
        {hoverHandlers => documentRow(hoverHandlers)}
      </AISuggestionPopover>
    );
  }

  return documentRow();
};

export default ReferencedDocument;
